import { chunk, range } from 'lodash';
import * as React from 'react';
import { useState } from 'react';
import { TabView } from 'react-native-tab-view';
import {
    DotNavigation,
    Icon,
    type IconType,
    SPACE_ICONS,
    View,
    XStack,
    YStack,
} from 'smoothie';

const PAGE_SIZE = 12;

interface IconSelectionProps {
    onSelect: (icon: string) => void;
    selectedIcon: string;
}

const IconSelection = ({ onSelect, selectedIcon }: IconSelectionProps) => {
    const [index, setIndex] = useState(0);

    // Array mapping index to set of icons
    const iconPages = chunk(Object.keys(SPACE_ICONS), PAGE_SIZE);
    const routes = range(iconPages.length).map((i) => ({ key: i.toString() }));

    const IconList = ({ page }: { page: number }) => {
        const icons = iconPages[page];
        return (
            <XStack
                alignItems="center"
                justifyContent="center"
                flexWrap="wrap"
                gap={0}
                rowGap={0}
                paddingHorizontal={16}
            >
                {icons?.map((icon, i) => (
                    <IconButton
                        key={i}
                        icon={icon}
                        isSelected={selectedIcon === icon}
                        onPress={() => {
                            onSelect(icon);
                        }}
                    />
                ))}
            </XStack>
        );
    };

    // TabView requires pure components for renderScene mapping -- proving a dynamic function requires
    // recreating the component and re-mounting, which leads to a bad experience
    // So the following is hard-coded to work for 2 pages of icons
    const renderScene = ({ route }: { route: { key: string } }) => {
        switch (route.key) {
            case '0':
                return <IconList page={0} />;
            case '1':
                return <IconList page={1} />;
            default:
                return null;
        }
    };

    // Note: TabView cannot be rendered within a Tamagui stack component
    return (
        <YStack width="100%" alignItems="center">
            <TabView
                navigationState={{ index, routes }}
                onIndexChange={setIndex}
                renderScene={renderScene}
                renderTabBar={() => null}
                initialLayout={{
                    height: 250,
                    width: 400,
                }}
                style={{
                    height: 250,
                    width: 400,
                }}
            />
            <DotNavigation
                total={iconPages.length}
                currentIndex={index}
                onSelectIndex={(i) => {
                    setIndex(i);
                }}
            />
        </YStack>
    );
};

interface IconSelectProps {
    icon: string;
    isSelected: boolean;
    onPress: () => void;
}

const IconButton = ({ icon, isSelected, onPress }: IconSelectProps) => {
    return (
        <View
            margin={4}
            padding={24}
            borderRadius={12}
            backgroundColor={isSelected ? '$green400' : '$gray100'}
            borderWidth={1}
            borderColor={isSelected ? '$green900' : '$gray200'}
            onPress={onPress}
        >
            <Icon
                icon={SPACE_ICONS[icon] as IconType}
                size={24}
                color={isSelected ? '$green900' : '$black'}
            />
        </View>
    );
};

export default IconSelection;
